import { FC, useRef, useState, useEffect, useMemo } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button } from 'primereact/button';
import { ReactComponent as BackIcon } from '$icons/uncolored/ArrowWithTail.svg';
import PreviewPDF from '$components/PreviewPDF/PreviewPdf';
import AdditionalVideosInternalScreen from '$components/Screens/AdditionalVideosScreen/AdditionalVideosInternal/AdditionalVideosInternal';
import NotesScreen from '$components/Screens/CandidateNotesScreen/NotesScreen/NotesScreen';

import ProfileMenu from './Menu/ProfileMenu';
import CandidateInformationCardEmployer from './CandidateCard/CandidateInformationCardEmployer';
import classes from './CandidateDetails.module.css';
import CandidateResume from './CandidateCard/CandidateResume/CandidateResume';
import UpdateToOfferSentConfirmModal from '../Positions/CandidateSummaryCardList/CandidateSummaryCard/UpdateToOfferSentConfirmModal/UpdateToOfferSentConfirmModal';
import ContactInformationModal from '../ContactInformationModal/ContactInformationModal';
import AdditionalDocuments from './AdditionalDocuments/AdditionalDocuments';
import WorkHistoryEditableContainerInternal from '../WorkHistoryEditableContainer/WorkHistoryEditableContainerInternal';
import EducationalInfoEditableContainerInternal from '../EducationalInfoEditableContainer/EducationalInfoEditableContainerInternal';
import SkillsEditableContainerInternal from '../SkillsEditableContainer/SkillsEditableContainerInternal';
import LanguageEditableContainerInternal from '../LanguageEditableContainer/LanguageEditableContainerInternal';
import ExtracurricularEditableContainerInternal from '../ExtracurricularEditableContainer/ExtracurricularEditableContainerInternal';
import JobInterestsEditableContainerInternal from '../JobInterestsEditableContainer/JobInterestsEditableContainerInternal';
import TechnicalInfoEditableContainerInternal from '../TechnicalInfoEditableContainer/TechnicalInfoEditableContainerInternal';
import WorkSummaryEditableContainer from '../WorkHistoryEditableContainer/WorkSummaryEditableContainer';
import { useGetFileCategoriesQuery } from '../../store/modules/form-info';
import { useGetAdditionalDocumentsByCandidateIdQuery } from '../../store/modules/additional-documents';
import { useGetWorkHistoryByCandidateIdQuery } from '../../store/modules/work-experience';
import { useAppSelector } from '../../hooks';
import { Candidate } from '../../types/candidates';
import AdditionalDocumentsFormCandidate from './AdditionalDocumentsFormCandidate/AdditionalDocumentsFormCandidate';
import { useOriginPath } from '../../hooks/use-origin-path';
import { CalendarModal } from '../Employers/CalendarModal/CalendarModal';
import CandidateFeedBackModal from '../Positions/CandidateSummaryCardList/CandidateFeedBackModal/CandidateFeedBackModal';
import CurriculumEditableContainer from '../CurriculumEditableContainer/CurriculumEditableContainer';
import CandidateCertifications from './CandidateCertifications/CandidateCertifications';
import AdditionalTags from './AdditionalTags/AdditionalTags';
import ScoresTabContainer from './ScoresTabContainer/ScoresTabContainer';

interface Props {
  secondary?: boolean;
  isEmployee?: boolean;
  candidate: Candidate;
}

export enum CandidateDetailsTabs {
  profile = 'Profile',
  videos = 'Videos',
  documents = 'Documents',
  resume = 'Resume',
  scores = 'Scores',
  notes = 'Notes',
}

const CandidateDetails: FC<Props> = ({ secondary, isEmployee, candidate }) => {
  const { getOriginPath } = useOriginPath();
  const [t, i18Next] = useTranslation('global');
  const { serverUser } = useAppSelector((state) => state.auth);

  const navRef = useRef<HTMLDivElement>(null);
  const [highlightedSection, setHighlightedSection] = useState(0);
  const [isCalendarModalOpen, setIsCalendarModalOpen] = useState(false);
  const [isFeedbackModalOpen, setIsFeedbackModalOpen] = useState(false);
  const [showContactModal, setShowContactModal] = useState<boolean>(false);
  const [showMakeOfferModal, setMakeOfferModal] = useState<boolean>(false);
  const [activeTab, setActiveTab] = useState(CandidateDetailsTabs.profile);

  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { candidateId, positionId } = useParams();
  const candidatePositionStatusId = candidate?.CandidatesPositionStatus?.find(
    (positionStatus) =>
      positionStatus?.Position?.Id === parseInt(positionId ?? '0', 10)
  )?.Id as number;

  const statusId = searchParams.get('statusId');
  const { data: fileCategories } = useGetFileCategoriesQuery({ t });

  const { data: workHistoryData } = useGetWorkHistoryByCandidateIdQuery({
    candidateId: +candidate.Id!,
    lang: i18Next.language as 'en' | 'es',
  });

  const { data: additionalDocuments } =
    useGetAdditionalDocumentsByCandidateIdQuery({
      lang: i18Next.language as 'en' | 'es',
      candidateId: +candidateId!,
    });

  const certificationFiles = useMemo(() => {
    return (
      additionalDocuments?.filter((singleDocument) => {
        return (
          singleDocument.FileCategory &&
          singleDocument.FileCategory.Id ===
            fileCategories?.find(
              (singleCategory) =>
                singleCategory.CategoryName.toLowerCase() === 'certification'
            )!.Id
        );
      }) || []
    );
  }, [additionalDocuments, fileCategories]);

  useEffect(() => {
    document.documentElement.style.setProperty(
      '--scroll-padding',
      `${navRef?.current?.offsetHeight}px`
    );
  }, [navRef]);

  const handleMakeOfferModal = (show: boolean) => setMakeOfferModal(show);

  const handleAdditionalVideos = () => {
    const route = isEmployee
      ? `/employer/video/${candidateId!}`
      : `/internal/video/${candidateId!}`;

    navigate(route);
  };

  const handleReject = () => {
    setIsFeedbackModalOpen(true);
  };

  return (
    <>
      <div
        className={`px-8 lg:px-12 flex-col md:flex-row mt-4 ${classes.CandidateDetailsContainer}`}
      >
        <div
          data-testid="profile-info-container"
          className={`${classes.infoContainer}`}
        >
          <div className="w-full">
            {(secondary || isEmployee) && (
              <button
                className={`mb-4 border border-solid py-2 font-semibold flex justify-center ${
                  secondary &&
                  'border-internal-main rounded w-full text-internal-main'
                } ${isEmployee && 'border-employer-main text-employer-main'}`}
                type="button"
                data-testid="back-button"
                onClick={() => {
                  const originPath = getOriginPath();
                  if (originPath !== null) {
                    navigate(originPath);
                    return;
                  }
                  navigate(-1);
                  if (window.history.length <= 1) {
                    navigate('/');
                  }
                }}
              >
                <span className="relative items-center flex">
                  <BackIcon
                    className={`rotate-180 absolute -left-16 ${
                      secondary && 'fill-internal-main'
                    } ${isEmployee && 'fill-employer-main'}`}
                  />
                  {t('CandidatesTable.Previous')}
                </span>
              </button>
            )}
          </div>
          <CandidateInformationCardEmployer
            candidate={candidate}
            secondary={secondary}
            candidateWorkExperience={workHistoryData}
            setShowModal={setShowContactModal}
          />

          {isEmployee && (
            <button
              type="button"
              className={classes.notesButton}
              style={{
                backgroundColor:
                  !secondary && isEmployee
                    ? 'var(--brand-blue-100)'
                    : 'var(--primary-green)',
              }}
              onClick={() => setIsCalendarModalOpen(true)}
            >
              {t('CandidatePositionSummaryCard.ScheduleInterview')}
            </button>
          )}

          {isEmployee && (
            <button
              type="button"
              className={classes.notesButton}
              style={{
                backgroundColor:
                  !secondary && isEmployee
                    ? 'var(--brand-blue-100)'
                    : 'var(--primary-green)',
              }}
              onClick={() => handleReject()}
            >
              {t('CandidatePositionSummaryCard.RejectApplication')}
            </button>
          )}

          {isEmployee && (
            <button
              type="button"
              className={`!bg-brand-blue100 ${classes.notesButton}`}
              onClick={handleAdditionalVideos}
            >
              {t('CandidatePositionSummaryCard.CandidateVideos')}
            </button>
          )}

          {isEmployee && (
            <CandidateResume
              resumeData={candidate?.Curriculum || {}}
              secondary={secondary}
            />
          )}
        </div>
        <div className={classes.formsContainer}>
          <div>
            {Object.keys(CandidateDetailsTabs).map((singleTab) => {
              const tabName = singleTab as keyof typeof CandidateDetailsTabs;

              let iconName = '';
              switch (CandidateDetailsTabs[tabName]) {
                case CandidateDetailsTabs.scores:
                  iconName = 'pi-chart-scatter';
                  break;
                case CandidateDetailsTabs.videos:
                  iconName = 'pi-video';
                  break;
                case CandidateDetailsTabs.profile:
                  iconName = 'pi-user';
                  break;
                case CandidateDetailsTabs.resume:
                  iconName = 'pi-file-pdf';
                  break;
                case CandidateDetailsTabs.documents:
                default:
                  iconName = 'pi-file';
                  break;
              }

              // Dont display the button if user dont have curriculum
              const hideCurriculumButton =
                CandidateDetailsTabs.resume === CandidateDetailsTabs[tabName] &&
                !candidate?.Curriculum?.Url;

              // Dont display notes button if is employer view
              const hideNoteButton =
                !secondary &&
                isEmployee &&
                CandidateDetailsTabs.notes === CandidateDetailsTabs[tabName];

              if (hideCurriculumButton || hideNoteButton) {
                return null;
              }

              return (
                <Button
                  data-testid={`tab_${tabName}`}
                  key={`tab_${tabName}`}
                  label={CandidateDetailsTabs[tabName]}
                  onClick={() => {
                    setActiveTab(CandidateDetailsTabs[tabName]);
                  }}
                  className={`rounded-none first:rounded-l last:rounded-r ${
                    activeTab === CandidateDetailsTabs[tabName]
                      ? 'bg-internal-main !text-white'
                      : 'bg-internal-main/20 text-brand-darkgrey hover:bg-internal-main/15'
                  } border-none`}
                  iconPos="right"
                  icon={`pi ${iconName}`}
                />
              );
            })}
          </div>

          {/* Profile Tab */}
          {activeTab === CandidateDetailsTabs.profile && (
            <>
              <ProfileMenu
                link={navRef}
                highlightedSectionIndex={highlightedSection}
                setHighlightedSection={setHighlightedSection}
                secondary={secondary}
              />

              {!!secondary && (
                <div>
                  <CurriculumEditableContainer
                    isActive={highlightedSection === 1}
                  />
                </div>
              )}
              <WorkSummaryEditableContainer
                isActive={false}
                candidate={candidate}
                secondary={!!secondary}
              />
              <div id="section-1">
                <WorkHistoryEditableContainerInternal
                  isActive={highlightedSection === 1}
                  secondary={!!secondary}
                />
              </div>
              <div id="section-2">
                <JobInterestsEditableContainerInternal
                  isActive={highlightedSection === 2}
                  secondary={!!secondary}
                  isEmployee={isEmployee}
                />
              </div>
              <div id="section-3">
                <SkillsEditableContainerInternal
                  isActive={highlightedSection === 3}
                  secondary={!!secondary}
                  candidateIsSimeraAICertified={candidate.IsSimeraAICertified}
                />
              </div>
              <div id="section-4">
                <EducationalInfoEditableContainerInternal
                  isActive={highlightedSection === 4}
                  secondary={!!secondary}
                />
              </div>
              <div id="section-5">
                <LanguageEditableContainerInternal
                  isActive={highlightedSection === 5}
                  secondary={!!secondary}
                />
              </div>
              <div id="section-6">
                <ExtracurricularEditableContainerInternal
                  isActive={highlightedSection === 6}
                  secondary={!!secondary}
                  candidate={candidate}
                />
              </div>
              <div id="section-7">
                <TechnicalInfoEditableContainerInternal
                  isActive={highlightedSection === 7}
                  secondary={!!secondary}
                />
              </div>
              {secondary && <AdditionalTags candidateId={candidate.Id} />}

              {/* <div id="section-111">
            <VideoShowcase candidate={candidate} />
          </div> */}
            </>
          )}
          {/* Videos Tab */}
          {activeTab === CandidateDetailsTabs.videos && (
            <section className="lg:px-8 bg-white rounded">
              <AdditionalVideosInternalScreen hideBackButton />
            </section>
          )}
          {/* Preview Resume */}
          {activeTab === CandidateDetailsTabs.resume && (
            <PreviewPDF pdfFileUrl={candidate.Curriculum.Url} hideBanner />
          )}
          {/* Documents Tab */}
          {activeTab === CandidateDetailsTabs.documents && (
            <>
              {(additionalDocuments?.length !== 0 || secondary) && (
                <div className={classes.ReadOnlyContainer} id="section-10">
                  <p className={classes.ReadOnlyTitle}>
                    {t('AdditionalDocs.Title')}
                  </p>
                  {secondary && (
                    <AdditionalDocumentsFormCandidate
                      candidateId={candidate?.Id!}
                      fileCategories={fileCategories!}
                      secondary={secondary}
                    />
                  )}
                </div>
              )}

              {secondary && fileCategories && (
                <CandidateCertifications
                  certificationFiles={certificationFiles}
                  candidateId={Number(candidateId)}
                  certificationFileCategoryId={
                    fileCategories?.find(
                      (singleCategory) =>
                        singleCategory.CategoryName.toLowerCase() ===
                        'certification'
                    )?.Id as number
                  }
                />
              )}

              {fileCategories &&
                (additionalDocuments?.length !== 0 || secondary) && (
                  <div className={classes.ReadOnlyContainer} id="section-10">
                    <p className={classes.ReadOnlyTitle}>
                      {t('AdditionalDocs.Title')}
                    </p>
                    <AdditionalDocuments
                      data={additionalDocuments || []}
                      fileCategories={fileCategories!}
                      secondary={secondary}
                    />
                  </div>
                )}
            </>
          )}
          {/* Scores Tab */}
          {activeTab === CandidateDetailsTabs.scores && (
            <ScoresTabContainer candidate={candidate} />
          )}
          {/* Notes Tab */}
          {activeTab === CandidateDetailsTabs.notes && (
            <div className="relative">
              <NotesScreen isTab />
            </div>
          )}
        </div>
      </div>
      {/* Modals */}
      {candidate && statusId && (
        <UpdateToOfferSentConfirmModal
          modalIsOpen={showMakeOfferModal}
          handleClose={handleMakeOfferModal}
          makeOfferToCandidateDto={{
            candidatePositionStatusId: +statusId,
            user: {
              name: `${serverUser?.FirstName} ${serverUser?.LastName}`,
              email: serverUser?.Email!,
            },
          }}
        />
      )}
      {showContactModal && (
        <ContactInformationModal
          candidate={candidate}
          setShowModal={setShowContactModal}
        />
      )}
      {isCalendarModalOpen && (
        <CalendarModal
          candidateId={candidate.Id}
          setIsOpen={setIsCalendarModalOpen}
          isOpen={isCalendarModalOpen}
          setSwitchingId={() => {}}
          candidatePositionStatusId={candidatePositionStatusId}
        />
      )}
      <CandidateFeedBackModal
        candidateId={candidate.Id}
        isOpen={isFeedbackModalOpen}
        setIsOpen={setIsFeedbackModalOpen}
        setSwitchingId={() => {}}
      />
    </>
  );
};

CandidateDetails.defaultProps = {
  secondary: false,
  isEmployee: false,
};

export default CandidateDetails;
